/** vendor */
import React, { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { sanitizeHTML } from '../../utils/inputSanitizer'

const NoteDialog = ({ 
  showDialog = false,
  note = undefined,
  onCreate = () => {}, 
  onCancel = () => {} 
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [noteTitle, setNoteTitle] = useState('')
  const [noteContent, setNoteContent] = useState('')
  const [editMode, setEditMode] = useState(!note)

  const validateTitle = () => {
    const validateTitlePattern = /^[a-zA-Z0-9\s]+$/
    return !noteTitle || validateTitlePattern.test(noteTitle)
  }

  const validateContent = () => {
    if (!noteContent) return false

    const hasTags = /<[^>]*>/.test(noteContent)
    const hasJS = /(on\w+\s*=|javascript:)/i.test(noteContent)
    const hasScript = /<\s*script/i.test(noteContent)
  
    return !(hasTags || hasJS || hasScript)
  }

  /**
   * Hooks
   */
  useEffect(() => {    
    setIsOpen(showDialog)
    
    if(note)
    {
      setNoteTitle(note?.title)
      setNoteContent(note?.content)
    }
    else
    {
      setNoteTitle('')
      setNoteContent('')
    }
  }, [showDialog, note])

  /**
   * Handlers
   */
  const handleTitleChange = (e) => {
    setNoteTitle(e.target.value)
  }

  const handleContentChange = (e) => {
    const cleanContent = e.target.value
      .replace(/<[^>]*>/gi, '')
      .replace(/(href|src)\s*=\s*["'][^"']*["']/gi, '') 

    setNoteContent(cleanContent)
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const clipboardData = e.clipboardData || window.clipboardData
    const html = clipboardData.getData('text/html') || clipboardData.getData('text/plain')
    const sanitized = sanitizeHTML(html)
  
    setNoteContent(sanitized)
  }

  const handleCancel = () => {
    setNoteTitle('')
    setIsOpen(false)
    onCancel()
  }

  const handleEdit = () => {
    let notes = localStorage.getItem('notes') 
    notes = notes ? JSON.parse(notes) : []

    let noteToEditIndex = notes.findIndex(n => n.id === note.id)

    note.title = noteTitle || `note ${noteToEditIndex + 1}`
    note.content = noteContent
    note.date = (new Date()).toLocaleDateString()

    notes[noteToEditIndex] = note

    localStorage.setItem('notes', JSON.stringify(notes)) 

    onCreate(notes)
  }

  const handleCreate = () => {
    let notes = localStorage.getItem('notes') 
    notes = notes ? JSON.parse(notes) : []

    notes.push({
      id: uuidv4(),
      title: noteTitle || `note ${notes.length + 1}`,
      content: noteContent,
      date: (new Date()).toLocaleDateString()
    })

    localStorage.setItem('notes', JSON.stringify(notes)) 

    onCreate(notes)
  }

  return (
    <div
      className={`fixed z-50 inset-0 overflow-y-auto ${
        isOpen ? 'flex' : 'hidden'
      } items-center justify-center`}
    >
      <div className="fixed inset-0 bg-an_green-dark opacity-60"></div>

      <div className="fixed bottom-0 left-0 w-full px-2">
        <div className="bg-an_white-dark rounded shadow-md w-full md:max-w-xl mx-auto z-50">
          <h2 className="w-full text-xl text-an_white flex justify-between items-center p-4 bg-an_green-dark rounded-t">
            <div className="text-an_white">
              {
                noteTitle ? noteTitle : 'make a note'
              } 
            </div>

            <div className="flex justify-end items-center">
                <label className="text-xs lowercase pr-1">edit</label>
                <div className="relative">
                    <div className="w-full flex justify-end">

                        <label className="relative inline-flex items-center cursor-pointer">
                            <input 
                              type="checkbox" 
                              value="editMode"
                              onChange={() => {
                                setEditMode(!editMode)
                              }}
                              checked={editMode} 
                              className="sr-only peer" 
                            />
                            <div 
                            
                            className={`
                                w-9 h-5 bg-an_orange-dark
                                rounded-full peer
                                peer-checked:after:translate-x-full peer-checked:after:border-an_white after:content-[''] 
                                after:absolute after:top-[2px] after:left-[2px] 
                                after:bg-an_white
                                    after:rounded-full after:h-4 after:w-4 after:transition-all 
                                peer-checked:bg-an_green

                            `}>&nbsp;</div>
                        </label>
                    </div>
                </div>
            </div>
          </h2>
          
          <div className="h-[72vh] overflow-y-auto">
            {
              !editMode &&
                <div className=" min-w-full justify-center p-4"> 
                  <h2>{noteTitle}</h2>
                  <p 
                    className="whitespace-pre-line" 
                    dangerouslySetInnerHTML={{ __html: noteContent }}
                  >             
                  
                  </p>
                </div>
            }

            {
              !noteTitle && !noteContent && !editMode &&
                <div className=" min-w-full justify-center p-4"> 
                  <p>
                    nothing here yet ...
                  </p>
                </div>
            }

            {
              editMode &&
                <div className="min-w-full justify-center p-4"> 
                  <div className="w-full">
                    <label htmlFor="noteTitle" className="block mb-2 text-base text-an_green-dark">title</label>
                    <div className="relative">
                        <input 
                            type="text" 
                            id="noteTitle"
                            value={noteTitle}
                            onChange={handleTitleChange} 
                            className={
                              `${noteTitle.length > 0 && !validateTitle() ? 'border-an_orange-dark' : ''} border rounded w-full text-xs py-2 px-3 text-an_green-dark`
                            }                              
                            placeholder={'note ' + (noteTitle.length + 1)}
                        />
                    </div>
                    <label className="block text-an_orange-dark mt-1">
                        { 
                            noteTitle.length > 0 && !validateTitle()
                                ? 'may only contain letters, numbers and spaces' 
                                : <>&nbsp;</>
                        }
                    </label>
                  </div>

                  <div className="mb-4 w-full">
                      <label htmlFor="description" className="block mb-2 text-sm font-medium text-black">note</label>
                      <textarea
                          resize="vertical" 
                          id="description" 
                          rows="12"
                          // onPaste={handlePaste}
                          onChange={handleContentChange}
                          value={noteContent} 
                          className="block p-2.5 w-full text-sm text-an_green-dark rounded border border-an_green-dark" 
                          placeholder="note"></textarea>
                  </div>
                </div>
            }
          </div>


        </div>

        <div className="flex justify-center w-full py-3 md:max-w-xl mx-auto">
            <button
              className="px-5 py-2 rounded w-36 text-an_green-dark bg-an_white mr-1"
              onClick={handleCancel}
            >
              cancel
            </button>

            {
              note &&
                <button
                  disabled={!validateTitle() || !validateContent()}
                  className={`${!validateTitle() || !validateContent() ? 'opacity-60' : ''} px-5 ml-1 py-2 relative rounded w-36 border border-an_orange-darker3 text-an_white bg-an_orange text-border-orange`}
                  onClick={handleEdit}
                >
                  <span>update</span> 
                  {
                    saving && 
                      <div className="animate-pulse absolute right-2 top-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 animate-spin ">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                      </div> 
                  }
                </button>
            }

            {
              !note &&
                <button
                  disabled={!validateTitle() || !validateContent()}
                  className={`${!validateTitle() || !validateContent() ? 'opacity-60' : ''} px-5 ml-1 py-2 relative rounded w-36 border border-an_orange-darker3 text-an_white bg-an_orange text-border-orange`}
                  onClick={handleCreate}
                >
                  <span>create</span> 
                  {
                    saving && 
                      <div className="animate-pulse absolute right-2 top-2">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 animate-spin ">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                      </div> 
                  }
                </button>
            }

        </div>
      </div>
    </div>
  )
}

export default NoteDialog
